import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, Combobox, Datepicker } from '@ibsys/kit';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import type {
  AttributeFilter,
  DashboardBlockId,
  DashboardQualityDisplaySettings,
  HeatmapMode,
  HiddenCriterionRef,
} from '@/entities/dashboards';
import { useTimeDisplayFormat } from '@/entities/settings';
import type { QualityZoneRanges } from '@/shared/lib';
import { AttributeFilterCell, AttributeFilterPicker, createEmptyFilter } from '@/features/attribute-filters';
import { DisplaySettings } from './DisplaySettings';

/**
 * Базовые фильтры дашборда качества (единый объект-state на странице).
 *
 * `dateFrom`/`dateTo` — границы периода в формате YYYY-MM-DD; пустая строка означает,
 * что граница не задана и не передаётся в запрос. Обе границы сравниваются с
 * `conversation.started_at`.
 */
export type DashboardFiltersState = {
  consultant: string;
  dateFrom: string;
  dateTo: string;
  heatmapMode: HeatmapMode;
};

type Props = {
  consultantNames: string[];
  /** Единый объект-state базовых фильтров */
  filters: DashboardFiltersState;
  /** Единый колбек обновления всего объекта фильтров */
  onChangeFilters: (next: DashboardFiltersState) => void;
  /** true — нужно показать кнопку «Сбросить drill-down» */
  hasDrill: boolean;
  /** Доступные атрибуты для добавления в панель фильтров (системная конфигурация) */
  availableAttributes: ConversationAttributeItem[];
  /** Активные атрибутные фильтры */
  activeAttributeFilters: AttributeFilter[];
  onChangeAttributeFilters: (filters: AttributeFilter[]) => void;
  onReset: () => void;
  onClearDrill: () => void;
  /** Пользовательские пороги зон мини-отчёта (настройка отображения). */
  zoneRanges: QualityZoneRanges;
  /** Критерии, скрытые из отображения (настройка отображения), — каждый со своим заключением. */
  hiddenCriteria: HiddenCriterionRef[];
  /** Идентификаторы скрытых блоков дашборда (настройка отображения). */
  hiddenBlocks: DashboardBlockId[];
  /** Колбэк изменения настроек отображения (полным объектом). */
  onChangeDisplay: (next: DashboardQualityDisplaySettings) => void;
};

/**
 * Панель фильтров дашборда качества.
 * Базовые фильтры (консультант, «Дата от», «Дата до», режим heatmap)
 * и атрибутные фильтры располагаются в одной общей сетке. Лейблом атрибутного
 * поля выступает имя атрибута, тип компонента выбирается по `Attribute.dataType`.
 *
 * «Дата от»/«Дата до» — независимые границы по полю самого разговора: обе сравниваются
 * с `conversation.started_at` — той же колонкой, по которой группируется ячейка тепловой
 * карты, иначе ячейка и её детализация расходятся. Параллельно остаётся доступным
 * атрибутный фильтр на DATE/TIMESTAMP-атрибуте — оба механизма работают через AND.
 */
export const QualityFilters: FC<Props> = ({
  consultantNames,
  filters,
  onChangeFilters,
  hasDrill,
  availableAttributes,
  activeAttributeFilters,
  onChangeAttributeFilters,
  onReset,
  onClearDrill,
  zoneRanges,
  hiddenCriteria,
  hiddenBlocks,
  onChangeDisplay,
}) => {
  const { t } = useTranslation('qualityDashboard');
  // Формат интервалов времени задаёт вид отбора по атрибуту-интервалу — тот же, что в таблицах
  const timeDisplayFormat = useTimeDisplayFormat('filter');

  const setField = <K extends keyof DashboardFiltersState>(key: K, value: DashboardFiltersState[K]) =>
    onChangeFilters({ ...filters, [key]: value });

  const consultantOptions = useMemo(
    () => [{ label: t('filters.all'), value: '' }, ...consultantNames.map((n) => ({ label: n, value: n }))],
    [consultantNames, t],
  );

  const heatmapOptions = useMemo(
    () => [
      { label: t('filters.byMonth'), value: 'month' as const },
      { label: t('filters.byWeek'), value: 'week' as const },
    ],
    [t],
  );

  // Карта атрибутов по `path` — единый ключ совпадает с `path` в attribute-filters
  const attributesByPath = useMemo(() => {
    const map = new Map<string, ConversationAttributeItem>();
    availableAttributes.forEach((a) => map.set(a.path, a));
    return map;
  }, [availableAttributes]);

  const activePaths = useMemo(() => new Set(activeAttributeFilters.map((f) => f.path)), [activeAttributeFilters]);

  const handleAddAttribute = (path: string) => {
    const attribute = attributesByPath.get(path);
    if (!attribute) return;
    const newFilter = createEmptyFilter(attribute, timeDisplayFormat);
    if (!newFilter) return;
    onChangeAttributeFilters([...activeAttributeFilters, newFilter]);
  };

  const handleUpdateFilter = (next: AttributeFilter) => {
    onChangeAttributeFilters(activeAttributeFilters.map((f) => (f.path === next.path ? next : f)));
  };

  const handleRemoveFilter = (path: string) => {
    onChangeAttributeFilters(activeAttributeFilters.filter((f) => f.path !== path));
  };

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 items-end">
          <Combobox
            label={t('filters.consultant')}
            value={filters.consultant}
            onChange={(v) => setField('consultant', String(v ?? ''))}
            options={consultantOptions}
            isSearchable
            className="w-full"
          />
          <Datepicker
            mode="single"
            outputType="iso-date"
            label={t('filters.dateFrom')}
            value={filters.dateFrom}
            onChange={(v) => setField('dateFrom', typeof v === 'string' ? v : '')}
          />
          <Datepicker
            mode="single"
            outputType="iso-date"
            label={t('filters.dateTo')}
            value={filters.dateTo}
            onChange={(v) => setField('dateTo', typeof v === 'string' ? v : '')}
          />
          <Combobox
            label={t('filters.heatmapMode')}
            value={filters.heatmapMode}
            onChange={(v) => setField('heatmapMode', (v as HeatmapMode) ?? 'month')}
            options={heatmapOptions}
            isSearchable={false}
            className="w-full"
          />
          {activeAttributeFilters.map((filter) => (
            <AttributeFilterCell
              key={filter.path}
              attribute={attributesByPath.get(filter.path)}
              filter={filter}
              onChange={handleUpdateFilter}
              onRemove={() => handleRemoveFilter(filter.path)}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 justify-between items-center">
          <AttributeFilterPicker
            availableAttributes={availableAttributes}
            activePaths={activePaths}
            onAdd={handleAddAttribute}
          />
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={onReset}>
              {t('filters.reset')}
            </Button>
            {hasDrill && (
              <Button variant="secondary" size="sm" onClick={onClearDrill}>
                {t('filters.clearDrill')}
              </Button>
            )}
          </div>
        </div>
        <DisplaySettings
          zoneRanges={zoneRanges}
          hiddenCriteria={hiddenCriteria}
          hiddenBlocks={hiddenBlocks}
          onChange={onChangeDisplay}
        />
      </CardContent>
    </Card>
  );
};
